/**
 * Profile — account settings
 *
 * Shows name, subscription tier (with tier-aware upgrade prompts), marks earned,
 * and founding circle badge if applicable. Tier display uses the new naming:
 *   free    → "Meet Yourself"
 *   growth  → "Practice"
 *   premium → "Awakening"
 *
 * Stripe integration:
 *   - Upgrade buttons call redirectToCheckout() → Stripe Checkout
 *   - "Manage billing" button calls redirectToPortal() → Stripe Billing Portal
 *   - ?upgraded=1 query param shows a success banner on return from Stripe
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { supabase, db } from '../lib/supabase'
import { redirectToCheckout, redirectToPortal } from '../lib/stripe'
import { trackUpgradePromptShown, trackUpgradeCompleted } from '../lib/analytics'
import { useLanguage, LANGUAGE_LABELS, type SupportedLanguage } from '../hooks/useLanguage'
import { pluralCategory } from '../lib/plural'
import { localizeArchetype } from '../lib/archetypes'
import NotificationSettings from '../components/NotificationSettings'

interface ProfilePageProps {
  user: User
}

interface SessionRow {
  phase_reached: number
  is_complete: boolean
}

// ── Archetype types (Phase 2 — Awakening tier only) ──────────────────────────
interface ArchetypeDefinition {
  id: string
  display_name: string
  description: string
  shadow_aspect: string
  integration_aspect: string
}

interface ArchetypeAssignment {
  id: string
  archetype_id: string
  state: string
  intensity: number
  observation_count: number
  user_confirmed: boolean
  user_rejected: boolean
  evidence_quote: string | null
  first_seen_at: string
}

// Combined assignment with definition data — what we render in the UI.
interface ArchetypeWithDef extends ArchetypeAssignment {
  definition: ArchetypeDefinition
}

interface SymbolDef {
  id: string
  minPhase: number
  path: React.ReactNode
}

const SYMBOLS: SymbolDef[] = [
  {
    id: 'traced',
    minPhase: 2,
    path: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 3v14M6 12l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'noticed',
    minPhase: 3,
    path: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" opacity="0.35" />
      </svg>
    ),
  },
  {
    id: 'shifted',
    minPhase: 4,
    path: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M5 12h14M14 7l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'chose',
    minPhase: 6,
    path: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M5 13l5 5L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
]

// ── Numerology ────────────────────────────────────────────────────────────────

function reduceToSingle(n: number): number {
  // Stop at master numbers 11, 22, 33; otherwise keep reducing
  if (n === 11 || n === 22 || n === 33) return n
  return n > 9 ? reduceToSingle(n % 10 + Math.floor(n / 10)) : n
}

function computeLifePath(month: number, day: number, year: number): number {
  const digits = `${month}${day}${year}`.split('').map(Number)
  return reduceToSingle(digits.reduce((a, b) => a + b, 0))
}

// ── Symbol accent colors — each mark has a distinct emotional resonance ────────
const SYMBOL_ACCENTS: Record<string, { color: string; bg: string }> = {
  traced:  { color: '#3A9B7A', bg: 'rgba(58,155,122,0.08)' },   // teal — body/somatic
  noticed: { color: '#7B40A8', bg: 'rgba(123,64,168,0.08)' },   // deep purple — awareness
  shifted: { color: '#B87D0A', bg: 'rgba(184,125,10,0.08)' },   // amber — insight
  chose:   { color: '#C45272', bg: 'rgba(196,82,114,0.08)' },   // rose — completion
}

// Monthly prices — shown as the secondary comparison on upgrade prompts
const TIER_PRICE: Record<string, string> = {
  growth:  '$14.99/mo',
  premium: '$29.99/mo',
}

// Annual prices — what the primary CTA offers. Annual leads per the pricing
// decision approved 2026-09-05; each is exactly half the 12-month total.
const TIER_PRICE_ANNUAL: Record<string, string> = {
  growth:  '$89.99/yr',
  premium: '$179.99/yr',
}

// ── Language selector ─────────────────────────────────────────────────────────

function LanguageSelector({ userId }: { userId: string }) {
  const { t } = useTranslation()
  const { currentLanguage, setLanguage } = useLanguage()
  const [saving, setSaving] = useState(false)

  async function handleChange(lang: SupportedLanguage) {
    setSaving(true)
    await setLanguage(userId, lang)
    setSaving(false)
  }

  return (
    <div className="px-5 py-4 rounded-2xl" style={{ background: 'white', border: '1.5px solid #E4D5EE' }}>
      <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B8FAA' }}>
        {t('profile.languageHeader')}
      </p>
      <div className="flex gap-2">
        {(Object.entries(LANGUAGE_LABELS) as [SupportedLanguage, string][]).map(([code, label]) => (
          <button
            key={code}
            onClick={() => handleChange(code)}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              background: currentLanguage === code ? '#3D1F62' : '#F4EFF8',
              color: currentLanguage === code ? 'white' : '#6B5F7A',
              border: 'none',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function ProfilePage({ user }: ProfilePageProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const [displayName, setDisplayName] = useState('')
  const [editName, setEditName] = useState('')
  const [tier, setTier] = useState('free')
  const [isFoundingMember, setIsFoundingMember] = useState(false)
  // When the founding price ends and the standard Awakening annual price applies.
  // Not when founding status ends — the badge and early access are permanent.
  const [foundingPriceEndsAt, setFoundingPriceEndsAt] = useState<Date | null>(null)
  const [periodEnd, setPeriodEnd] = useState<Date | null>(null)
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [lifePathNumber, setLifePathNumber] = useState<number | null>(null)
  const [showLifePathDetail, setShowLifePathDetail] = useState(false)
  const [birthEditMonth, setBirthEditMonth] = useState(0)
  const [birthEditDay, setBirthEditDay] = useState(0)
  const [birthEditYear, setBirthEditYear] = useState('')
  const [editingBirthday, setEditingBirthday] = useState(false)
  const [savingBirthday, setSavingBirthday] = useState(false)
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [stripeError, setStripeError] = useState<string | null>(null)
  // Separate error state for the billing portal so the error renders inline
  // next to the button rather than at the top of the page (which the user
  // won't see after scrolling down to click "Manage billing").
  const [portalError, setPortalError] = useState<string | null>(null)
  // Phase 2 — Archetype panel (Awakening only). Top 6 by intensity, non-rejected,
  // states emerging/active/integrating only (released archetypes hidden in profile).
  const [archetypes, setArchetypes] = useState<ArchetypeWithDef[]>([])
  // Which archetype card is expanded (shows shadow + integration aspects)
  const [expandedArchetype, setExpandedArchetype] = useState<string | null>(null)

  // Success banner when returning from Stripe Checkout (?upgraded=1)
  const justUpgraded = searchParams.get('upgraded') === '1'

  // Fire upgrade_completed once when the user lands back from Stripe with their new tier loaded
  useEffect(() => {
    if (!justUpgraded || loading) return
    const upgradedTier = tier as 'growth' | 'premium'
    if (upgradedTier === 'growth' || upgradedTier === 'premium') {
      trackUpgradeCompleted(upgradedTier, 'month')
    }
  }, [justUpgraded, loading, tier])

  // Stripe webhooks arrive asynchronously — the DB may still have the old tier when the
  // browser lands on this page after checkout. Poll every 1.5s for up to 7.5s until the
  // tier updates, then re-render with the correct subscription state.
  useEffect(() => {
    if (!justUpgraded || loading) return
    let attempts = 0
    const poll = setInterval(async () => {
      attempts++
      const { data } = await db()
        .from('profiles')
        .select('subscription_tier, is_founding_member, founding_price_ends_at, subscription_current_period_end')
        .eq('id', user.id)
        .maybeSingle()
      const row = data as {
        subscription_tier: string
        is_founding_member: boolean | null
        founding_price_ends_at: string | null
        subscription_current_period_end: string | null
      } | null
      const newTier = row?.subscription_tier ?? 'free'
      // Stop when a paid tier is confirmed or after 5 attempts (~7.5s)
      if (newTier === 'growth' || newTier === 'premium' || attempts >= 5) {
        setTier(newTier)
        if (row?.is_founding_member != null) setIsFoundingMember(row.is_founding_member)
        if (row?.founding_price_ends_at) setFoundingPriceEndsAt(new Date(row.founding_price_ends_at))
        if (row?.subscription_current_period_end) setPeriodEnd(new Date(row.subscription_current_period_end))
        clearInterval(poll)
      }
    }, 1500)
    return () => clearInterval(poll)
  }, [justUpgraded, loading, user.id]) // tier intentionally excluded — runs once on load, not per-update

  useEffect(() => {
    Promise.all([
      db()
        .from('profiles')
        .select('display_name, subscription_tier, is_founding_member, founding_price_ends_at, stripe_customer_id, subscription_current_period_end, birth_month, birth_day, birth_year')
        .eq('id', user.id)
        .maybeSingle(),
      db()
        .from('sessions')
        .select('phase_reached, is_complete')
        .eq('user_id', user.id),
    ]).then(([profileRes, sessionsRes]) => {
      const row = profileRes.data as {
        display_name: string | null
        subscription_tier: string
        is_founding_member: boolean | null
        founding_price_ends_at: string | null
        stripe_customer_id: string | null
        subscription_current_period_end: string | null
        birth_month: number | null
        birth_day: number | null
        birth_year: number | null
      } | null

      setDisplayName(row?.display_name ?? '')
      setEditName(row?.display_name ?? '')
      const loadedTier = row?.subscription_tier ?? 'free'
      setTier(loadedTier)
      setIsFoundingMember(row?.is_founding_member ?? false)
      setHasStripeCustomer(!!row?.stripe_customer_id)
      if (row?.founding_price_ends_at) {
        setFoundingPriceEndsAt(new Date(row.founding_price_ends_at))
      }
      if (row?.subscription_current_period_end) {
        setPeriodEnd(new Date(row.subscription_current_period_end))
      }
      if (row?.birth_month && row?.birth_day && row?.birth_year) {
        setLifePathNumber(computeLifePath(row.birth_month, row.birth_day, row.birth_year))
      }
      // Pre-populate birthday edit fields from any data already saved in Onboarding
      setBirthEditMonth(row?.birth_month ?? 0)
      setBirthEditDay(row?.birth_day ?? 0)
      setBirthEditYear(row?.birth_year ? String(row.birth_year) : '')
      setSessions((sessionsRes.data as SessionRow[]) ?? [])
      setLoading(false)

      // Phase 2 — Load active/integrating archetypes for the Awakening panel.
      // Top 6 by intensity, excluding released and user-rejected. Two-step query
      // because Supabase JS doesn't support cross-table joins on custom schemas.
      if (loadedTier === 'premium') {
        db()
          .from('user_archetype_assignments')
          .select('id, archetype_id, state, intensity, observation_count, user_confirmed, user_rejected, evidence_quote, first_seen_at')
          .eq('user_id', user.id)
          .eq('user_rejected', false)
          .in('state', ['emerging', 'active', 'integrating'])
          .gte('intensity', 0.85)   // only surface archetypes the classifier is confident about
          .order('intensity', { ascending: false })
          .limit(6)
          .then(async ({ data: assignments }) => {
            const list = (assignments as ArchetypeAssignment[] | null) ?? []
            if (list.length === 0) return
            const ids = list.map(a => a.archetype_id)
            const { data: defs } = await db()
              .from('archetype_definitions')
              .select('id, display_name, description, shadow_aspect, integration_aspect')
              .in('id', ids)
            // Overlay the locale's copy so a Polish user reads the pattern in Polish.
            const defMap = new Map((defs as ArchetypeDefinition[] ?? []).map(d => [d.id, localizeArchetype(d.id, d, t)]))
            setArchetypes(
              list
                .filter(a => defMap.has(a.archetype_id))
                .map(a => ({ ...a, definition: defMap.get(a.archetype_id)! }))
            )
          })
      }
    })
  }, [user.id])

  async function handleSaveBirthday() {
    const yr = parseInt(birthEditYear, 10)
    if (!birthEditMonth || !birthEditDay || isNaN(yr) || yr < 1900 || yr > new Date().getFullYear()) return
    setSavingBirthday(true)
    await db()
      .from('profiles')
      .upsert({ id: user.id, birth_month: birthEditMonth, birth_day: birthEditDay, birth_year: yr }, { onConflict: 'id' })
    setLifePathNumber(computeLifePath(birthEditMonth, birthEditDay, yr))
    setEditingBirthday(false)
    setSavingBirthday(false)
  }

  async function handleSaveName() {
    const trimmed = editName.trim()
    if (!trimmed || saving || trimmed.length > 100) return
    setSaving(true)
    await db()
      .from('profiles')
      .upsert({ id: user.id, display_name: trimmed }, { onConflict: 'id' })
    setDisplayName(trimmed)
    setEditing(false)
    setSaving(false)
  }

  // ── Archetype confirm / reject ───────────────────────────────────────────
  async function handleConfirmArchetype(id: string) {
    await db().from('user_archetype_assignments').update({ user_confirmed: true }).eq('id', id)
    setArchetypes(prev => prev.map(a => a.id === id ? { ...a, user_confirmed: true } : a))
  }

  async function handleRejectArchetype(id: string) {
    await db().from('user_archetype_assignments').update({ user_rejected: true }).eq('id', id)
    setArchetypes(prev => prev.filter(a => a.id !== id))
    if (expandedArchetype === id) setExpandedArchetype(null)
  }

  async function handleSignOut() {
    await supabase?.auth.signOut()
    navigate('/')
  }

  /** Redirects to Stripe Checkout for an upgrade. Shows inline error on failure. */
  async function handleUpgrade(upgradeTier: 'growth' | 'premium', interval: 'month' | 'year' = 'month') {
    setCheckoutLoading(true)
    setStripeError(null)
    trackUpgradePromptShown(upgradeTier, 'profile')
    const { error } = await redirectToCheckout(upgradeTier, interval)
    if (error) { setStripeError(error); setCheckoutLoading(false) }
    // On success window.location.href changes — no cleanup needed
  }

  /** Opens Stripe Billing Portal for subscription management. */
  async function handleManageBilling() {
    setPortalLoading(true)
    setPortalError(null)
    const { error } = await redirectToPortal()
    if (error) { setPortalError(error); setPortalLoading(false) }
    // On success window.location.href changes — no cleanup needed
  }

  // Sessions remaining for free tier (out of 3)
  const completedSessions = sessions.filter(s => s.is_complete).length
  const freeSessionsRemaining = tier === 'free' ? Math.max(0, 3 - completedSessions) : null

  /*
   * Two different things, which used to be one and were wrong because of it.
   *
   * Founding STATUS is permanent: the badge, new arcs at release ahead of
   * general availability, input to Marta on arc development. Those were sold
   * as the part that never expires, so the badge is gated on the flag alone.
   *
   * The founding PRICE is not. It covers three annual payments and then the
   * standard Awakening annual price applies, which is what the countdown line
   * below is about.
   */
  const foundingPriceLockActive = !!foundingPriceEndsAt && new Date() < foundingPriceEndsAt

  // Subscription card accent — matches tier identity
  const tierAccent = tier === 'premium' ? '#B87D0A' : tier === 'growth' ? '#3D1F62' : '#9B8FAA'
  const tierAccentBg = tier === 'premium' ? 'rgba(184,125,10,0.04)' : tier === 'growth' ? 'rgba(61,31,98,0.04)' : 'rgba(155,114,192,0.03)'

  if (loading) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: '#FAF7F4' }}>
        <div className="w-5 h-5 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-svh flex flex-col" style={{ background: 'radial-gradient(ellipse at 55% 0%, rgba(155,114,192,0.1) 0%, transparent 55%), #FAF7F4' }}>

      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6B5F7A' }}
          className="hover:opacity-70 transition-opacity"
          aria-label={t('profile.back')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="font-display text-lg" style={{ color: '#1C1128', fontWeight: 400 }}>{t('profile.account')}</h1>
      </header>

      <main className="flex-1 px-6 pb-16 max-w-xl mx-auto w-full space-y-4 page-enter">

        {/* Upgrade success banner — shown when returning from Stripe Checkout */}
        {justUpgraded && (
          <div className="rounded-2xl px-5 py-4 flex items-center gap-3 message-appear"
            style={{ background: '#E8F5E9', border: '1.5px solid #A5D6A7' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
              <path d="M3.5 9.5l4 4L14.5 5" stroke="#2E7D32" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-sm font-medium" style={{ color: '#2E7D32' }}>
              {t('profile.welcomeTo', { tier: tier === 'premium' ? t('common.awakening') : tier === 'growth' ? t('common.practice') : t('profile.yourSubscription') })}
              {t('profile.accessActive')}
            </p>
          </div>
        )}

        {/* Stripe error banner — shown if checkout or portal redirect fails */}
        {stripeError && (
          <div className="rounded-2xl px-5 py-4" style={{ background: '#FFF3E0', border: '1.5px solid #FFCC80' }}>
            <p className="text-sm" style={{ color: '#E65100' }}>{stripeError}</p>
          </div>
        )}

        {/* Name */}
        <div className="rounded-2xl px-5 py-5" style={{
          background: 'rgba(155,114,192,0.04)',
          borderTop: '3px solid #9B72C0',
          borderRight: '1.5px solid #E4D5EE',
          borderBottom: '1.5px solid #E4D5EE',
          borderLeft: '1.5px solid #E4D5EE',
        }}>
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
            {t('profile.yourName')}
          </p>

          {editing ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName() }}
                maxLength={100}
                autoFocus
                className="flex-1 px-3 py-2.5 rounded-xl border text-sm focus:outline-none"
                style={{ borderColor: '#9B72C0', background: 'white', color: '#1C1128' }}
              />
              <button
                onClick={handleSaveName}
                disabled={!editName.trim() || saving}
                className="px-4 py-2.5 rounded-xl text-sm font-medium"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {saving ? '...' : t('common.save')}
              </button>
              <button
                onClick={() => { setEditing(false); setEditName(displayName) }}
                className="px-3 py-2.5 rounded-xl text-sm"
                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-base" style={{ color: displayName ? '#1C1128' : '#9B8FAA' }}>
                {displayName || t('common.notSet')}
              </p>
              <button
                onClick={() => setEditing(true)}
                className="text-xs px-3 py-1.5 rounded-lg"
                style={{ background: '#F4EFF8', color: '#6B3FA0', border: 'none', cursor: 'pointer' }}
              >
                {t('common.edit')}
              </button>
            </div>
          )}
        </div>

        {/* Email */}
        <div className="rounded-2xl px-5 py-5" style={{
          background: 'rgba(74,128,192,0.04)',
          borderTop: '3px solid #4A80C0',
          borderRight: '1.5px solid #E4D5EE',
          borderBottom: '1.5px solid #E4D5EE',
          borderLeft: '1.5px solid #E4D5EE',
        }}>
          <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: '#4A80C0' }}>
            {t('common.email')}
          </p>
          <p className="text-sm" style={{ color: '#6B5F7A' }}>{user.email}</p>
        </div>

        {/* Life Path Number — invitation card when birthday is incomplete, revealed card when all three fields are set */}
        {lifePathNumber === null ? (
          <div className="rounded-2xl px-5 py-5" style={{
            background: 'rgba(123,64,168,0.04)',
            borderTop: '3px solid #7B40A8',
            borderRight: '1.5px solid #E4D5EE',
            borderBottom: '1.5px solid #E4D5EE',
            borderLeft: '1.5px solid #E4D5EE',
          }}>
            <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#7B40A8' }}>
              {t('profile.lifePath')}
            </p>
            {!editingBirthday ? (
              <>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
                  {t('profile.lifePathBody')}
                </p>
                <button
                  onClick={() => setEditingBirthday(true)}
                  className="text-xs px-3 py-1.5 rounded-lg"
                  style={{ background: '#F4EFF8', color: '#6B3FA0', border: 'none', cursor: 'pointer' }}
                >
                  {t('profile.addBirthday')}
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-xs mb-1" style={{ color: '#9B8FAA' }}>
                  {t('profile.monthDayYear')}
                </p>
                <div className="flex gap-2">
                  <select
                    value={birthEditMonth}
                    onChange={e => setBirthEditMonth(Number(e.target.value))}
                    className="flex-1 px-2 py-2.5 rounded-xl border text-sm focus:outline-none"
                    style={{ borderColor: '#9B72C0', background: 'white', color: birthEditMonth ? '#1C1128' : '#9B8FAA' }}
                  >
                    <option value={0}>{t('profile.birthMonth')}</option>
                    {Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(i18n.language, { month: 'short' })).map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={birthEditDay}
                    onChange={e => setBirthEditDay(Number(e.target.value))}
                    className="flex-1 px-2 py-2.5 rounded-xl border text-sm focus:outline-none"
                    style={{ borderColor: '#9B72C0', background: 'white', color: birthEditDay ? '#1C1128' : '#9B8FAA' }}
                  >
                    <option value={0}>{t('profile.birthDay')}</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={t('profile.year')}
                    value={birthEditYear}
                    onChange={e => setBirthEditYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4}
                    className="w-20 px-3 py-2.5 rounded-xl border text-sm focus:outline-none"
                    style={{ borderColor: '#9B72C0', background: 'white', color: '#1C1128' }}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveBirthday}
                    disabled={savingBirthday || !birthEditMonth || !birthEditDay || birthEditYear.length !== 4}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium"
                    style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: savingBirthday ? 'wait' : 'pointer', opacity: (!birthEditMonth || !birthEditDay || birthEditYear.length !== 4) ? 0.45 : 1 }}
                  >
                    {savingBirthday ? '...' : t('profile.revealLifePath')}
                  </button>
                  <button
                    onClick={() => setEditingBirthday(false)}
                    className="px-4 py-2.5 rounded-xl text-sm"
                    style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (() => {
          // Life path copy lives in the locale files (profile.lifePaths.<n>) so the
          // i18n parity test guards it; lifePathNumber is always one of the 12 keys.
          const lifePathKey = `profile.lifePaths.${lifePathNumber}`
          const meaning = { name: t(`${lifePathKey}.name`), description: t(`${lifePathKey}.description`) }
          const rawDetail = t(`${lifePathKey}.detail`, { returnObjects: true })
          const detail = Array.isArray(rawDetail) ? (rawDetail as string[]) : null
          return (
            <div className="rounded-2xl px-5 py-5" style={{
              background: 'rgba(123,64,168,0.04)',
              borderTop: '3px solid #7B40A8',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#7B40A8' }}>
                {t('profile.lifePath')}
              </p>

              <div className="flex items-start gap-4">
                {/* The number — large and prominent */}
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #7B40A8 0%, #9B72C0 100%)', boxShadow: '0 4px 16px rgba(123,64,168,0.25)' }}
                >
                  <span className="font-display text-2xl" style={{ color: 'white', fontWeight: 500 }}>
                    {lifePathNumber}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium mb-1" style={{ color: '#1C1128' }}>{meaning.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{meaning.description}</p>
                </div>
              </div>

              {/* Expand toggle */}
              <button
                onClick={() => setShowLifePathDetail(v => !v)}
                className="mt-4 flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-70"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6B3FA0' }}
              >
                {showLifePathDetail ? t('common.close') : t('profile.whatThisMeans')}
                <svg
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                  style={{ transform: showLifePathDetail ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Expanded detail — three paragraphs: core energy, gift, shadow + inner work */}
              {showLifePathDetail && detail && (
                <div className="mt-4 pt-4 flex flex-col gap-3 message-appear" style={{ borderTop: '1px solid #F0EAF7' }}>
                  {detail.map((para, i) => (
                    <p key={i} className="text-sm leading-relaxed" style={{ color: i === 2 ? '#9B72C0' : '#6B5F7A' }}>
                      {para}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )
        })()}

        {/* Notifications: this device's push toggle and the optional categories */}
        <NotificationSettings />

        {/* Subscription */}
        <div className="rounded-2xl px-5 py-5" style={{
          background: tierAccentBg,
          borderTop: `3px solid ${tierAccent}`,
          borderRight: '1.5px solid #E4D5EE',
          borderBottom: '1.5px solid #E4D5EE',
          borderLeft: '1.5px solid #E4D5EE',
        }}>
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: tierAccent }}>
            {t('profile.subscription')}
          </p>

          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-base font-medium" style={{ color: '#1C1128' }}>
                  {t(`profile.tiers.${tier}`, { defaultValue: tier })}
                </p>
                {/* Founding Circle badge — permanent, never tied to the price lock */}
                {isFoundingMember && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide"
                    style={{ background: 'linear-gradient(135deg, #D4AF37, #F0D060)', color: '#5A3E00' }}>
                    {t('profile.foundingCircle')}
                  </span>
                )}
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#9B8FAA', lineHeight: 1.5 }}>
                {t(`profile.tierDesc.${tier}`, { defaultValue: '' })}
              </p>
              {/* How long the founding price still runs. Disappears once it has. */}
              {foundingPriceLockActive && foundingPriceEndsAt && (
                <p className="text-xs mt-1" style={{ color: '#C4A830' }}>
                  {t('profile.foundingLockedUntil', { date: foundingPriceEndsAt.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }) })}
                </p>
              )}
            </div>

            {/* Tier pill badge */}
            {tier === 'free' && (
              <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: '#F4EFF8', color: '#9B72C0', border: '1px solid #E4D5EE' }}>
                {t('common.free')}
              </span>
            )}
            {tier === 'growth' && (
              <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: '#3D1F62', color: 'white' }}>
                {t('profile.tiers.growth')}
              </span>
            )}
            {tier === 'premium' && (
              <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #7C4500, #C4A830)', color: 'white' }}>
                {t('profile.tiers.premium')}
              </span>
            )}
          </div>

          {/* Free tier: sessions remaining + progress dots */}
          {tier === 'free' && freeSessionsRemaining !== null && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid #F0EAF7' }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs" style={{ color: '#9B8FAA' }}>
                  {freeSessionsRemaining > 0
                    ? t(`profile.freeSessionsRemaining.${pluralCategory(freeSessionsRemaining, i18n.language)}`, { count: freeSessionsRemaining })
                    : t('profile.allFreeComplete')}
                </p>
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="rounded-full" style={{
                      width: i < completedSessions ? 8 : 6,
                      height: i < completedSessions ? 8 : 6,
                      background: i < completedSessions ? '#9B72C0' : '#E4D5EE',
                    }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Renewal / cancellation date for paid tiers */}
          {(tier === 'growth' || tier === 'premium') && periodEnd && (
            <p className="text-xs mt-3" style={{ color: '#9B8FAA' }}>
              {t('profile.renews', { date: periodEnd.toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' }) })}
            </p>
          )}

          {/* Upgrade CTAs — call Stripe Checkout */}
          {tier === 'free' && (
            <div className="mt-4 flex flex-col gap-2">
              {/* Annual leads, monthly is the comparison — approved 2026-09-05,
                  PRICING_TQO_TIA.md section 12. Monthly stays visible one tap
                  away; hiding it is what would make this a dark pattern. */}
              <button
                onClick={() => handleUpgrade('growth', 'year')}
                disabled={checkoutLoading}
                className="w-full py-3 rounded-xl text-sm font-medium"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer' }}
              >
                {checkoutLoading ? t('common.openingCheckout') : t('profile.upgradeToPracticeAnnual', { price: TIER_PRICE_ANNUAL.growth })}
              </button>
              <button
                onClick={() => handleUpgrade('growth', 'month')}
                disabled={checkoutLoading}
                className="w-full py-2.5 rounded-xl text-sm"
                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: checkoutLoading ? 'wait' : 'pointer' }}
              >
                {t('profile.orMonthly', { price: TIER_PRICE.growth })}
              </button>
            </div>
          )}
          {tier === 'growth' && (
            <div className="mt-4">
              {/* Navigate to the dedicated upgrade page rather than going
                  directly to Stripe — lets the user see the arc preview
                  and choose monthly vs annual before committing. */}
              <button
                onClick={() => navigate('/upgrade/awakening')}
                className="w-full py-3 rounded-xl text-sm font-medium"
                style={{ background: 'linear-gradient(135deg, #5C2D8A 0%, #9B72C0 100%)', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('arcs.exploreAwakening')} →
              </button>
            </div>
          )}

          {/* Manage billing — only for users who have a Stripe customer (ever subscribed) */}
          {hasStripeCustomer && (
            <div className="mt-3">
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="w-full py-2.5 rounded-xl text-sm"
                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: portalLoading ? 'wait' : 'pointer' }}
              >
                {portalLoading ? t('common.opening') : t('profile.manageBilling')}
              </button>
              {/* Inline error — rendered here so it's visible without scrolling up */}
              {portalError && (
                <p className="text-xs mt-2 text-center" style={{ color: '#E65100' }}>{portalError}</p>
              )}
            </div>
          )}
        </div>

        {/* Symbol wall */}
        {sessions.length > 0 && (() => {
          const counts = SYMBOLS.reduce<Record<string, number>>((acc, sym) => {
            acc[sym.id] = sessions.filter(s => s.phase_reached >= sym.minPhase).length
            return acc
          }, {})
          const anyEarned = SYMBOLS.some(s => counts[s.id] > 0)
          if (!anyEarned) return null
          return (
            <div className="rounded-2xl px-5 py-5" style={{
              background: 'rgba(107,63,160,0.03)',
              borderTop: '3px solid #6B3FA0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: '#6B3FA0' }}>
                {t('profile.marksEarned')}
              </p>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: '#9B8FAA' }}>
                {t('profile.marksBody')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {SYMBOLS.map(sym => {
                  const count = counts[sym.id]
                  const earned = count > 0
                  const accent = SYMBOL_ACCENTS[sym.id]
                  return (
                    <div
                      key={sym.id}
                      className="flex flex-row items-center gap-3 py-3 px-4 rounded-xl"
                      style={{
                        background: earned && accent ? accent.bg : 'rgba(244,239,248,0.5)',
                        border: earned && accent ? `1px solid ${accent.color}30` : '1px solid #F0ECF5',
                        opacity: earned ? 1 : 0.45,
                      }}
                    >
                      {/* Icon */}
                      <span className="flex-shrink-0" style={{ color: earned && accent ? accent.color : '#C4B5D4' }}>
                        {sym.path}
                      </span>
                      {/* Label + description + count */}
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-medium leading-none" style={{ color: earned && accent ? accent.color : '#9B8FAA' }}>
                            {t(`profile.marks.${sym.id}.label`)}
                          </p>
                          {earned && accent && (
                            <p className="text-[10px] font-display leading-none" style={{ color: accent.color }}>
                              ×{count}
                            </p>
                          )}
                        </div>
                        <p className="text-[11px] leading-snug" style={{ color: earned ? '#6B5F7A' : '#C4B5D4' }}>
                          {t(`profile.marks.${sym.id}.description`)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* ── Patterns I'm Holding (Awakening tier only) ─────────────────── */}
        {/* Expandable archetype cards — confidence ≥ 0.85.                 */}
        {/* Each card shows the archetype name + state badge. Tap to expand  */}
        {/* for the full description, shadow aspect, integration path, and   */}
        {/* confirm / dismiss actions.                                       */}
        {tier === 'premium' && archetypes.length > 0 && (
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#7B40A8' }}>
              {t('profile.patternsHolding')}
            </p>
            <div className="flex flex-col gap-3">
              {archetypes.map(a => {
                const isExpanded = expandedArchetype === a.id
                const stateColor = a.state === 'active' ? '#7B40A8'
                  : a.state === 'integrating' ? '#B87D0A'
                  : '#C4B5D4'
                return (
                  <div
                    key={a.id}
                    className="rounded-2xl overflow-hidden"
                    style={{
                      background: a.user_confirmed ? 'rgba(123,64,168,0.04)' : 'white',
                      border: `1.5px solid ${a.user_confirmed ? 'rgba(123,64,168,0.2)' : '#E4D5EE'}`,
                    }}
                  >
                    {/* Card header — always visible, tap to expand */}
                    <button
                      onClick={() => setExpandedArchetype(isExpanded ? null : a.id)}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      {/* State dot */}
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: stateColor }} />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" style={{ color: '#1C1128' }}>
                          {a.definition.display_name}
                          {a.user_confirmed && (
                            <span className="ml-1.5 text-[10px]" style={{ color: '#7B40A8' }}>{t('profile.confirmed')}</span>
                          )}
                        </p>
                        {!isExpanded && (
                          <p className="text-xs mt-0.5 line-clamp-1" style={{ color: '#9B8FAA' }}>
                            {a.definition.description}
                          </p>
                        )}
                      </div>

                      {/* State badge */}
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{
                          background: a.state === 'active' ? 'rgba(123,64,168,0.1)' : 'rgba(196,181,212,0.15)',
                          color: a.state === 'active' ? '#7B40A8' : '#9B8FAA',
                          border: '1px solid #E4D5EE',
                        }}
                      >
                        {a.state.charAt(0).toUpperCase() + a.state.slice(1)}
                      </span>

                      {/* Expand chevron */}
                      <svg
                        width="14" height="14" viewBox="0 0 14 14" fill="none"
                        style={{ flexShrink: 0, color: '#C4B5D4', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                      >
                        <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1" style={{ borderTop: '1px solid #F3EDE6' }}>
                        {/* Description */}
                        <p className="text-sm mb-4" style={{ color: '#4B3B5A', lineHeight: 1.7 }}>
                          {a.definition.description}
                        </p>

                        {/* Shadow aspect */}
                        <div className="mb-3">
                          <p className="text-[10px] font-medium uppercase tracking-widest mb-1" style={{ color: '#C4B5D4' }}>
                            {t('profile.woundBeneath')}
                          </p>
                          <p className="text-sm" style={{ color: '#6B5F7A', lineHeight: 1.65, fontStyle: 'italic' }}>
                            {a.definition.shadow_aspect}
                          </p>
                        </div>

                        {/* Integration */}
                        <div className="mb-4">
                          <p className="text-[10px] font-medium uppercase tracking-widest mb-1" style={{ color: '#9B72C0' }}>
                            {t('profile.integrationLooks')}
                          </p>
                          <p className="text-sm" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
                            {a.definition.integration_aspect}
                          </p>
                        </div>

                        {/* Evidence quote if available */}
                        {a.evidence_quote && (
                          <p
                            className="text-xs mb-4 px-3 py-2.5 rounded-xl"
                            style={{ color: '#9B8FAA', background: '#FAF7F4', fontStyle: 'italic', lineHeight: 1.6 }}
                          >
                            "{a.evidence_quote}"
                          </p>
                        )}

                        {/* Confirm / Dismiss */}
                        {!a.user_confirmed && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmArchetype(a.id)}
                              className="flex-1 py-2.5 rounded-xl text-sm font-medium"
                              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                            >
                              {t('profile.thisResonates')}
                            </button>
                            <button
                              onClick={() => handleRejectArchetype(a.id)}
                              className="px-4 py-2.5 rounded-xl text-sm"
                              style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#9B8FAA', cursor: 'pointer' }}
                            >
                              {t('profile.notMe')}
                            </button>
                          </div>
                        )}
                        {a.user_confirmed && (
                          <div className="flex items-center justify-between">
                            <p className="text-xs" style={{ color: '#9B72C0' }}>{t('profile.youClaimed')}</p>
                            <button
                              onClick={() => handleRejectArchetype(a.id)}
                              className="text-xs px-3 py-1.5 rounded-lg"
                              style={{ background: 'transparent', border: '1px solid #E4D5EE', color: '#C4B5D4', cursor: 'pointer' }}
                            >
                              {t('common.remove')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <p className="text-xs mt-3" style={{ color: '#C4B5D4' }}>
              {t(`profile.seenAcross.${pluralCategory(archetypes[0]?.observation_count ?? 0, i18n.language)}`, { count: archetypes[0]?.observation_count ?? 0 })}
            </p>
          </div>
        )}

        {/* Language selector */}
        <LanguageSelector userId={user.id} />

        {/* Legal links — Terms, Privacy, Support live on the marketing site, not in the app */}
        <div className="flex items-center justify-center gap-4 pt-1">
          {[
            { key: 'legalTerms', path: 'terms' },
            { key: 'legalPrivacy', path: 'privacy' },
            { key: 'legalSupport', path: 'support' },
          ].map(({ key, path }) => (
            <a
              key={path}
              href={`https://the-inner-awakening.com/${i18n.language.startsWith('pl') ? 'pl/' : ''}${path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs"
              style={{ color: '#C4B5D4' }}
            >
              {t(`profile.${key}`)}
            </a>
          ))}
        </div>

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          className="w-full py-3 rounded-xl text-sm"
          style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
        >
          {t('auth.signOut')}
        </button>

      </main>
    </div>
  )
}
